import { useContext, useCallback } from 'react';
import { useCampaignsApiFacade } from '../../../../apis/facades/useCampaignsApiFacade';
import { CampaignMemberStatus } from '../canvas-hubspot-page/campaign-dialog/campaignTypes';
import { CampaignMemberStatusContext } from './CampaignMemberStatusContext';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';
import { useHubspotWorkflows } from '../canvas-hubspot-page/workflow-dialog/useHubspotWorkflows';

export const useCampaignMemberStatuses = () => {
  const {
    patch_campaign_member_status,
    get_campaign_members_status,
    delete_campaign_member_status,
  } = useCampaignsApiFacade();
  const {
    campaignMemberStatuses,
    setCampaignMemberStatuses,
    loadStatus,
    setLoadStatus,
    initialized,
  } = useContext(CampaignMemberStatusContext);
  const { getWorkflows } = useHubspotWorkflows();

  const { errorHandlingBuilder } = useErrorHandling();

  if (!initialized) {
    throw new Error('Please wrap your component with CampaignMemberStatusProvider');
  }

  // Update the status of a campaign member
  // The IsDefault is unique, so we need to update the other statuses to false
  // if the new status is default
  const updateCampaignMemberStatus = useCallback(
    async ({
      Id,
      HasResponded,
      IsDefault,
    }: Pick<CampaignMemberStatus, 'IsDefault' | 'HasResponded' | 'Id'>) => {
      const exitingStatus = campaignMemberStatuses.find((status) => status.Id === Id);
      if (!exitingStatus) {
        return;
      }
      const newStatus = { ...exitingStatus, HasResponded, IsDefault };

      const newStatuses = campaignMemberStatuses.map((status) => {
        if (status.Id === Id) {
          return newStatus;
        }
        if (IsDefault) {
          status.IsDefault = false;
        }
        return status;
      });
      setCampaignMemberStatuses(newStatuses);
      await patch_campaign_member_status(newStatus);
    },
    [campaignMemberStatuses, patch_campaign_member_status, setCampaignMemberStatuses],
  );

  const loadCampaignMemberStatuses = useCallback(
    async (campaignId: string) => {
      errorHandlingBuilder()
        .withErrorNotification('Error retrieving status field from salesforce')
        .execute(async () => {
          setLoadStatus('loading');
          const statuses = await get_campaign_members_status(campaignId);
          setCampaignMemberStatuses(statuses);
          setLoadStatus('success');
        })
        .catch(() => {
          setLoadStatus('error');
        });
    },
    [errorHandlingBuilder, get_campaign_members_status, setCampaignMemberStatuses, setLoadStatus],
  );
  const replaceCampaignMemberStatus = useCallback(
    async ({
      campaignId,
      oldStatusId,
      newStatusId,
    }: {
      campaignId: string;
      oldStatusId: string;
      newStatusId: string;
    }) => {
      const oldStatus = campaignMemberStatuses.find((status) => status.Id === oldStatusId);
      const newStatus = campaignMemberStatuses.find((status) => status.Id === newStatusId);

      if (!oldStatus || !newStatus) {
        return;
      }
      errorHandlingBuilder()
        .withErrorNotification('Error replacing status')
        .execute(async () => {
          await delete_campaign_member_status(campaignId, {
            newStatusName: newStatus.Label,
            newStatusId,
            oldStatusId,
            oldStatusName: oldStatus.Label,
          });
          await getWorkflows();
        })
        .catch(() => {
          setLoadStatus('error');
        });
    },
    [
      campaignMemberStatuses,
      delete_campaign_member_status,
      errorHandlingBuilder,
      getWorkflows,
      setLoadStatus,
    ],
  );

  return {
    campaignMemberStatuses,
    updateCampaignMemberStatus,
    loadCampaignMemberStatuses,
    replaceCampaignMemberStatus,
    loadStatus,
  };
};
